import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './Terms.module.css';
import PublicSiteHeader from './PublicSiteHeader';

function AccordionToggleIcon({ expanded }) {
  return (
    <span className={styles.accordionIcon} aria-hidden>
      <svg
        className={styles.accordionIconSvg}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        {expanded ? (
          <path d="M5 12h14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
        ) : (
          <>
            <path d="M12 5v14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
            <path d="M5 12h14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
          </>
        )}
      </svg>
    </span>
  );
}

const Terms = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const accordionData = [
    {
      id: 'partnerships',
      title: 'How Our Partnerships Work in Plain Language (FAQ)',
      content: `At Richuel, we connect you with top-tier financial professionals when you're ready for personalized guidance—without the hassle of searching on your own.

<br/><br/><strong>Our Vetting Process</strong>

<br/>We don't partner with just anyone. Our network includes only carefully vetted Certified Financial Planners™ (CFPs®), coaches, and advisors selected based on:
<br/>* Years of relevant experience with clients like you (mid-career shifters, GenX/Millennials)
<br/>* Strong credentials and clean regulatory records
<br/>* Excellent feedback from clients and professional peers
<br/>* Alignment with our focus on practical, anxiety-reducing financial guidance

<br/><br/><strong>How Referrals Work</strong>

<br/><strong>1. Your choice:</strong> When our tools show you might benefit from expert input, we'll ask if you'd like an introduction.

<br/><br/><strong>2. Explicit consent:</strong> You control what (if any) details we share—such as high-level goals, household situation, or specific questions.

<br/><br/><strong>3. Seamless handoff:</strong> We make the connection via email, chat, or scheduling link. From there, it's direct between you and the professional.

<br/><br/><strong>4. Their terms apply:</strong> Once connected, they handle their own services, fees, and privacy practices (always review theirs first).

<br/><br/><strong>Transparency First</strong>

<br/>* We'll always disclose if we receive compensation for successful referrals (like many platforms do).
<br/>* No pressure—engaging (or not) is 100% your decision.
<br/>* We maintain these relationships to ensure you get quality support that complements our planning tools.

<br/><br/>Questions? Email <strong>team@richuel.com</strong>. Your trust is everything to us.`
    },
    {
      id: '1',
      title: '1. About Richuel and the Platform',
      content: `Richuel is a digital financial planning platform designed to help users explore scenarios related to income, expenses, savings, and other life and career decisions through planning tools, calculators, and AI-powered guidance.

<br/><br/>The Platform may include, without limitation:
<br/>* Interactive planning tools, projections, and calculators
<br/>* Scenario analysis and visualizations
<br/>* Educational content, articles, podcasts, or guides
<br/>* Access to financial planners, coaches, therapists, and other professionals through referrals or introductions
<br/>* Communication channels such as chat, messaging, or video calls

<br/><br/>Richuel may update, modify, suspend, or discontinue any part of the Platform at any time, with or without notice, and will not be liable to you or any third party for doing so.`
    },
    {
      id: '2',
      title: '2. No Investment, Legal, or Tax Advice',
      content: `Richuel is not a registered investment adviser, broker-dealer, tax advisor, or law firm, and does not provide individualized investment, legal, or tax advice.

<br/><br/>* All tools, projections, educational materials, and outputs from the Platform are for general informational and educational purposes only and are not tailored to any specific individual's investment objectives, financial situation, or needs.
<br/>* Nothing on the Platform should be construed as a recommendation, offer, or solicitation to buy or sell any security or to adopt any particular investment, tax, legal, or financial strategy.
<br/>* Any hypothetical or projected outcomes are estimates only, are subject to significant uncertainty, and may differ materially from actual results.

<br/><br/>You are solely responsible for your financial decisions. Before making any investment, tax, legal, or other financial decisions, you should consult with appropriately licensed professionals who have a fiduciary duty to you where applicable.`
    },
    {
      id: '3',
      title: '3. Referrals and Third-Party Providers',
      content: `From time to time, Richuel may refer or introduce you to third-party providers, including but not limited to:
<br/>* Certified Financial Planner™ professionals and other advisers who may be registered investment advisers or associated with registered investment adviser firms
<br/>* Coaches, therapists, or other wellness providers
<br/>* Financial institutions, platforms, or other service providers (collectively, "Third-Party Providers").

<br/><br/><strong>3.1 Relationship to Third-Party Providers</strong>

<br/>* Richuel is not a party to any agreement you enter into with a Third-Party Provider and is not responsible for the services they provide.
<br/>* Third-Party Providers are solely responsible for their own services, fees, disclosures, regulatory obligations, and compliance.
<br/>* Richuel does not supervise or control the professional judgment of any Third-Party Provider and does not guarantee any particular outcome from their services.

<br/><br/>Richuel maintains a curated network of Third-Party Providers and uses commercially reasonable criteria—such as professional experience, credentials, regulatory or professional standing, and ongoing client or peer feedback—to select and retain providers in its network.

<br/><br/>Any personalized investment advice, investment management, or regulated financial advice you receive will be provided exclusively by the applicable Third-Party Provider under a separate agreement between you and that provider.

<br/><br/><strong>3.2 Sharing of Information and Consent</strong>

<br/>Richuel will only share your personal information with Third-Party Providers with your explicit consent and in accordance with our Privacy Policy.

<br/><br/>* By requesting a referral, introduction, or other service involving a Third-Party Provider, you expressly authorize Richuel to share relevant information you provide (such as your name, contact information, high-level financial details, and stated goals) with that provider.
<br/>* Once your information is shared, it will be subject to the Third-Party Provider's own privacy and data handling practices, which may differ from ours. You should review the provider's privacy policy and terms before engaging them.

<br/><br/><strong>3.3 Compensation and Conflicts of Interest</strong>

<br/>Richuel may be compensated by Third-Party Providers for referrals, introductions, or other services provided to them, and may also charge users subscription or usage fees for certain Platform features.

<br/><br/>* If Richuel receives compensation or has a financial interest related to a referral or recommendation, Richuel will disclose that fact to you as required by applicable law, so that you can make an informed decision.
<br/>* You are under no obligation to engage with any Third-Party Provider, whether or not Richuel may receive compensation, and any decision to do so is solely yours.`
    },
    {
      id: '4',
      title: '4. Eligibility and Account Registration',
      content: `You must be at least 18 years old and capable of entering into a legally binding contract to use the Platform.

<br/><br/>To access certain features, you may need to create an account ("Account") and provide accurate, current, and complete information. You agree to:
<br/>* Maintain and promptly update your Account information
<br/>* Keep your login credentials confidential
<br/>* Accept responsibility for all activities that occur under your Account

<br/><br/>You must notify Richuel immediately if you believe your Account or credentials have been compromised. Richuel may suspend or terminate your Account for any violation of these Terms or for other reasons at its sole discretion.`
    },
    {
      id: '5',
      title: '5. User Responsibilities and Acceptable Use',
      content: `You may use the Platform only for lawful purposes and in accordance with these Terms.

<br/><br/>You agree not to:
<br/>* Use the Platform in any manner that violates applicable law or regulation, including securities, privacy, and consumer protection laws
<br/>* Provide false, misleading, or incomplete information
<br/>* Upload, transmit, or distribute any content that is unlawful, harmful, abusive, harassing, defamatory, obscene, invasive of privacy, infringing, or otherwise objectionable
<br/>* Introduce viruses, malware, or other harmful code, or attempt to gain unauthorized access to the Platform or related systems
<br/>* Reverse engineer, decompile, or attempt to derive the source code of any part of the Platform, except to the extent such restriction is prohibited by law
<br/>* Use automated tools (such as scrapers, robots, or spiders) to access the Platform without prior written consent
<br/>* Use the Platform for commercial purposes other than those expressly permitted by Richuel in writing

<br/><br/>Richuel may monitor use of the Platform and remove or restrict access to any content or user that violates these Terms or applicable law.`
    },
    {
      id: '6',
      title: '6. Financial Tools, Projections, and Educational Content',
      content: `The Platform's calculators, projections, visualizations, models, AI-generated outputs, and other tools are intended as informational and educational aids.

<br/><br/>* Results and outputs are hypothetical and based on assumptions and information you provide, which may be incomplete or inaccurate.
<br/>* These tools do not constitute a comprehensive financial plan and should not be relied upon as the sole basis for any financial decisions.
<br/>* Past performance, if shown, does not guarantee future results, and no projection or scenario can assure any particular outcome.

<br/><br/>Richuel and its affiliates are not responsible for any decisions or actions you take based on the outputs or content provided by the Platform. You remain fully responsible for evaluating your options with appropriate professional advisers.`
    },
    {
      id: '7',
      title: '7. Privacy and Data Security',
      content: `Your use of the Platform is subject to our Privacy Policy, which describes how Richuel collects, uses, shares, and safeguards your personal information.

<br/><br/>Richuel uses commercially reasonable administrative, technical, and physical safeguards designed to protect your information from unauthorized access, use, or disclosure, recognizing that no system can be guaranteed to be completely secure.

<br/><br/>You acknowledge that:
<br/>* Transmission of data over the internet involves risks outside Richuel's control
<br/>* Richuel cannot guarantee the absolute security of information transmitted to or from the Platform
<br/>* You use the Platform and provide information at your own risk`
    },
    {
      id: '8',
      title: '8. Intellectual Property',
      content: `The Platform and all content and materials provided through it—including text, graphics, logos, icons, images, software, tools, models, and layouts (collectively, "Content")—are owned by or licensed to Richuel and are protected by intellectual property laws.

<br/><br/>Subject to these Terms, Richuel grants you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Platform and Content for your personal, non-commercial use.

<br/><br/>You may not:
<br/>* Copy, reproduce, modify, distribute, publicly display, or create derivative works from the Platform or Content except as expressly permitted
<br/>* Remove or alter any copyright, trademark, or other proprietary notices
<br/>* Use any trademarks, service marks, or logos appearing on the Platform without Richuel's prior written permission`
    },
    {
      id: '9',
      title: '9. User Content',
      content: `To the extent the Platform allows you to submit, upload, or post content (such as comments, feedback, or data you provide beyond required inputs) ("User Content"), you retain ownership of your User Content.

<br/><br/>By submitting User Content, you grant Richuel a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, and display such User Content in connection with operating, improving, and promoting the Platform, subject to our Privacy Policy.

<br/><br/>You represent and warrant that:
<br/>* You have all rights necessary to grant the above license
<br/>* Your User Content does not infringe or violate any third-party rights or applicable law
<br/>* Your User Content is accurate and not misleading

<br/><br/>Richuel may remove or disable access to any User Content at its discretion.`
    },
    {
      id: '10',
      title: '10. Third-Party Links and Content',
      content: `The Platform may contain links to third-party websites, apps, or services that are not owned or controlled by Richuel.

<br/><br/>* Richuel does not endorse or assume responsibility for any third-party content, products, services, or practices.
<br/>* Your use of third-party sites and services is subject to their own terms and privacy policies.

<br/><br/>Any access to or use of third-party sites is at your own risk, and Richuel will not be liable for any loss or damage arising from such use.`
    },
    {
      id: '11',
      title: '11. Disclaimers',
      content: `THE PLATFORM AND ALL CONTENT, TOOLS, AND SERVICES PROVIDED THROUGH IT ARE PROVIDED "AS IS," "AS AVAILABLE," AND "WITH ALL FAULTS," WITHOUT ANY WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED.

<br/><br/>TO THE MAXIMUM EXTENT PERMITTED BY LAW, RICHUEL AND ITS AFFILIATES DISCLAIM ALL WARRANTIES, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, ACCURACY, AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE.

<br/><br/>Without limiting the foregoing, Richuel does not warrant that:
<br/>* The Platform will be uninterrupted, timely, secure, or error-free
<br/>* Any projections, scenarios, or outputs will be accurate, complete, or reflective of actual future results
<br/>* Any defects will be corrected`
    },
    {
      id: '12',
      title: '12. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL RICHUEL OR ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE PLATFORM OR ANY CONTENT OR SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

<br/><br/>To the extent any liability is not fully disclaimed, in no event shall Richuel's total aggregate liability arising out of or related to these Terms or your use of the Platform exceed the greater of: (a) the amounts you have paid to Richuel for use of the Platform in the twelve (12) months preceding the claim, if any; or (b) one hundred U.S. dollars (US $100).

<br/><br/>Some jurisdictions do not allow certain disclaimers or limitations of liability, so some of the above limitations may not apply to you, and you may have additional rights.`
    },
    {
      id: '13',
      title: '13. Indemnification',
      content: `You agree to indemnify, defend, and hold harmless Richuel and its affiliates, officers, directors, employees, contractors, and agents from and against any and all claims, demands, losses, liabilities, damages, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to:
<br/>* Your use of or access to the Platform
<br/>* Your violation of these Terms or any applicable law
<br/>* Your User Content
<br/>* Your interactions or relationships with Third-Party Providers

<br/><br/>Richuel reserves the right, at your expense, to assume the exclusive defense and control of any matter subject to indemnification, and you agree to cooperate with Richuel's defense of such claims.`
    },
    {
      id: '14',
      title: '14. Modifications to the Terms',
      content: `Richuel may revise these Terms from time to time. The "Last Updated" date at the top indicates when the Terms were last changed.

<br/><br/>If changes are material, Richuel may provide additional notice as required by law. Your continued use of the Platform after any changes become effective constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the Platform.`
    },
    {
      id: '15',
      title: '15. Termination',
      content: `Richuel may, in its sole discretion and without liability, suspend or terminate your access to all or part of the Platform at any time, including for any actual or suspected violation of these Terms, legal requirements, or suspected fraud or misuse.

<br/><br/>Upon termination, your right to use the Platform will immediately cease, but sections of these Terms that by their nature should survive (including, without limitation, Sections 2–3 and 7–18) will remain in effect.`
    },
    {
      id: '16',
      title: '16. Governing Law and Dispute Resolution',
      content: `These Terms and your use of the Platform will be governed by and construed in accordance with the laws of the State of California, without regard to its conflicts of law principles.

<br/><br/><strong>16.1 Informal Resolution</strong>

<br/>Before initiating formal dispute resolution, you agree to first contact Richuel at <strong>legal@richuel.com</strong> and attempt to resolve the dispute informally in good faith.

<br/><br/><strong>16.2 Binding Arbitration and Class Action Waiver</strong>

<br/>To the fullest extent permitted by law, any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Platform that cannot be resolved informally will be resolved by binding individual arbitration, and not in court.

<br/><br/>* You and Richuel each waive any right to a jury trial or to participate in a class, collective, or representative action.
<br/>* Arbitration will be administered by a recognized arbitration provider under its applicable rules for consumer disputes, as modified by these Terms.
<br/>* The place of arbitration will be in the State of California, unless otherwise required by law or agreed by the parties.

<br/><br/>If this class action waiver is found unenforceable with respect to a particular claim, that claim must be brought in a court of competent jurisdiction, and not in arbitration.

<br/><br/>Some users may have rights under applicable law that override portions of this arbitration agreement; in such cases, those laws will control to the extent they apply.`
    },
    {
      id: '17',
      title: '17. Miscellaneous',
      content: `* <strong>Entire Agreement.</strong> These Terms, together with the Privacy Policy and any additional terms or disclosures provided to you, constitute the entire agreement between you and Richuel regarding the Platform and supersede all prior or contemporaneous understandings relating to such subject matter.

<br/><br/>* <strong>Severability.</strong> If any provision of these Terms is found to be invalid or unenforceable, that provision will be enforced to the maximum extent permissible, and the remaining provisions will remain in full force and effect.

<br/><br/>* <strong>No Waiver.</strong> Any failure by Richuel to enforce any provision of these Terms will not be deemed a waiver of future enforcement of that or any other provision.

<br/><br/>* <strong>Assignment.</strong> You may not assign or transfer these Terms or any rights or obligations hereunder without Richuel's prior written consent. Richuel may assign or transfer these Terms without restriction.

<br/><br/>* <strong>Contact.</strong> Questions about these Terms or the Platform can be sent to: <strong>team@richuel.com</strong> or to Richuel, Inc. at a mailing address that Richuel may specify on the Platform.`
    },
  ];

  return (
    <div className={styles.termsContainer}>
      <PublicSiteHeader linkLogoToHome onLogin={handleLogin} onSignup={handleSignup} />

      <main className={styles.mainContent} id="terms-main" aria-labelledby="terms-page-title">
        <div className={styles.contentWrapper}>
          <h1 id="terms-page-title" className={styles.title}>
            Richuel Terms of Use
          </h1>

          <div className={styles.policyContent}>
            <p className={`${styles.description} ${styles.descriptionMeta}`}>
              <strong>Last Updated: January 5, 2026</strong>
            </p>

            <p className={`${styles.description} ${styles.descriptionIntro}`}>
              Welcome to the Richuel website, applications, and related services (collectively, the
              &quot;Platform&quot;). The Platform is operated by Richuel, Inc. (&quot;Richuel,&quot;
              &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). The term &quot;you&quot; refers to
              any visitor, user, or account holder accessing or using the Platform.
            </p>

            <p className={`${styles.description} ${styles.descriptionAgree}`}>
              By accessing or using the Platform, you agree to be bound by these Terms of Use (the
              &quot;Terms&quot;) and our{' '}
              <Link to="/privacy-policy" className={styles.inlineLink}>
                Privacy Policy
              </Link>
              , which is incorporated by reference. If you do not agree to these Terms, you may not
              access or use the Platform.
            </p>

            <section className={styles.accordionSection} aria-labelledby="accordion-heading">
              <h2 id="accordion-heading" className={styles.accordionTitle}>
                Terms of Use
              </h2>
              <div className={styles.accordionContainer} role="list">
                {accordionData.map((section, index) => {
                  const expanded = Boolean(expandedSections[section.id]);
                  return (
                    <div
                      key={section.id}
                      className={`${styles.accordionItem}${expanded ? ` ${styles.accordionItemOpen}` : ''}`}
                      role="listitem"
                      style={{ '--item-index': index }}
                    >
                      <button
                        type="button"
                        className={styles.accordionHeader}
                        onClick={() => toggleSection(section.id)}
                        aria-expanded={expanded}
                        aria-controls={`terms-section-${section.id}`}
                        id={`terms-trigger-${section.id}`}
                      >
                        <span className={styles.accordionQuestion}>{section.title}</span>
                        <AccordionToggleIcon expanded={expanded} />
                      </button>
                      <div
                        id={`terms-section-${section.id}`}
                        className={`${styles.accordionPanel}${expanded ? ` ${styles.accordionPanelOpen}` : ''}`}
                        role="region"
                        aria-labelledby={`terms-trigger-${section.id}`}
                        aria-hidden={!expanded}
                        inert={!expanded}
                      >
                        <div className={styles.accordionPanelInner}>
                          <div className={styles.accordionContent}>
                            <div
                              className={styles.accordionBody}
                              dangerouslySetInnerHTML={{ __html: section.content }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
